/**
 * Multi Select Filter
 * Tips:
 *  - remember to adjust the fuse settings, especially the property keys, depending on listed elements
 *
 * @param {array} items - Array of all items to display
 * @param {array} itemsUuids - Array of selected items uuids
 * @param {function} handleSave - Handler function responsible for Save button
 * @param {function} handleChangeCheckbox - Handler function responsible for returning selected itemsUuids
 * @param {string | JSX.Element} label - Label of checkbox item
 * @param {array} fuseSettings - Array of fuseSettings
 * @param {array} doubleToggleButtonOptions - Array representing doubleToggleButton labels ex. [{label: "label", uuid: "label"}]
 * @param {string} checkboxIndeterminateLabel - Label of checkboxIndeterminate
 *
 */

import { Fragment, useState } from 'react'

import SearchBox from 'components/SearchBox'
import DoubleToggleButton from 'components/DoubleToggleButton'
import CheckboxIndeterminate from 'components/CheckboxIndeterminate'

import { selectableListItems } from 'utils/selectableList'

const MultiSelectFilter = ({
  items,
  itemsUuids,
  handleSave,
  handleChangeCheckbox,
  label,
  fuseSettings,
  doubleToggleButtonOptions,
  checkboxIndeterminateLabel,
}) => {
  // states
  const [selectedFilterOption, setSelectedFilterOption] =
    useState('all')
  const [searchQuery, setSearchQuery] = useState('')

  // displayed items on the list
  const {
    filteredItemsBySelectedFilterOption,
    checkedItemsWithinFilteredItemsUuids,
    itemsSelectedNotDisplayed,
    isCheckAll,
    isIndeterminate,
    isDisabled,
  } = selectableListItems(
    items,
    searchQuery,
    selectedFilterOption,
    itemsUuids,
    fuseSettings
  )

  // handlers
  const handleCheckAllItems = () => {
    // if some items are selected, select all
    if (
      checkedItemsWithinFilteredItemsUuids?.length <
      filteredItemsBySelectedFilterOption?.length
    ) {
      handleChangeCheckbox([
        ...filteredItemsBySelectedFilterOption?.map(
          (item) => item?.uuid
        ),
        ...itemsSelectedNotDisplayed,
      ])
      return {
        checked: true,
        indeterminate: false,
      }
      // if all items are selected, unselect all
    } else if (
      checkedItemsWithinFilteredItemsUuids?.length ===
      filteredItemsBySelectedFilterOption?.length
    ) {
      handleChangeCheckbox(itemsSelectedNotDisplayed)
      return {
        checked: false,
        indeterminate: false,
      }
      // if some items are selected, select all
    } else if (checkedItemsWithinFilteredItemsUuids?.length === 0) {
      return {
        checked: false,
        indeterminate: true,
      }
    }
  }

  return (
    <div className="modal-body">
      <div className="item-settings">
        <section className="selectable-items">
          <SearchBox
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search..."
          />
          <div className="selectable-items__searchbox">
            <DoubleToggleButton
              options={doubleToggleButtonOptions}
              selectedUuid={selectedFilterOption}
              onChange={setSelectedFilterOption}
            />
          </div>
          <CheckboxIndeterminate
            handleChange={handleCheckAllItems}
            checked={isCheckAll}
            indeterminate={isIndeterminate}
            label={checkboxIndeterminateLabel}
            className={
              isCheckAll ? '-checkbox-indeterminate-checked' : ''
            }
            disabled={isDisabled}
          />
          {
            <div className="selectable-items-list">
              {filteredItemsBySelectedFilterOption?.map(
                (item, index) => (
                  <Fragment key={item.uuid}>
                    {label(item, index)}
                  </Fragment>
                )
              )}
            </div>
          }
        </section>
        <footer className="selectable-items__footer">
          <button
            className="btn -primary selectable-items__submit-btn"
            onClick={handleSave}
          >
            Save
          </button>
        </footer>
      </div>
    </div>
  )
}

export default MultiSelectFilter
